import { t } from "@/lib/i18n/interpolate";
import {
  type DocBlock,
  type DocModel,
  type DocSection,
  listItemsOf,
} from "@/lib/tool-kit/research-workflow/document";
import {
  containsPhrase,
  wordsOf,
} from "@/lib/tool-kit/research-workflow/plain-language";

/**
 * A qualitative interview guide (interview protocol).
 *
 * The anatomy is the one methods texts teach: an introduction script and a consent
 * reminder read before the recorder starts, easy warm-up questions, the main questions
 * grouped by topic with follow-ups and probes, and a closing that hands the floor back
 * to the participant. Rubin and Rubin's *Qualitative Interviewing* draws the three-way
 * distinction the guide follows — main questions open a topic, follow-up questions
 * pursue something the participant said, probes keep them talking without steering.
 *
 * A semi-structured guide groups the questions by topic and lets the interviewer move
 * between them; a structured one fixes the wording and the order, numbers the questions
 * straight through, and limits probing to neutral prompts so every participant hears
 * the same interview.
 */

export type InterviewType = "semi" | "structured";
export type InterviewRecording = "audio" | "video" | "none";

export interface InterviewTopic {
  id: string;
  name: string;
  /** The researcher's own main questions, one per line. Empty → templates. */
  questions: string;
}

export interface InterviewInput {
  title: string;
  researchQuestion: string;
  /** What the study is about, in words a participant would use. */
  about: string;
  interviewer: string;
  institution: string;
  type: InterviewType;
  /** Already formatted for the reader's locale, or empty. */
  minutes: string;
  recording: InterviewRecording;
  /** Extra warm-up questions, one per line. */
  warmUp: string;
  topics: InterviewTopic[];
}

export interface InterviewPhrases {
  docTitle: string;
  metaStudy: string;
  metaQuestion: string;
  metaType: string;
  metaLength: string;
  typeSemi: string;
  typeStructured: string;
  beforeHeading: string;
  beforeItems: readonly string[];
  introHeading: string;
  introThanks: string;
  introNameInstitution: string;
  introName: string;
  introAbout: string;
  introTime: string;
  introNoRight: string;
  introStructured: string;
  consentHeading: string;
  consentRead: string;
  consentVoluntary: string;
  consentAudio: string;
  consentVideo: string;
  consentNotes: string;
  consentStart: string;
  warmUpHeading: string;
  warmUpDefaults: readonly string[];
  topicHeading: string;
  mainLead: string;
  followUpLead: string;
  mainTemplates: readonly string[];
  followUpTemplates: readonly string[];
  probesHeading: string;
  probesLead: string;
  probes: readonly string[];
  structuredHeading: string;
  structuredLead: string;
  structuredProbes: readonly string[];
  closingHeading: string;
  closingItems: readonly string[];
  afterHeading: string;
  afterItems: readonly string[];
  missingTitle: string;
  missingQuestion: string;
  missingAbout: string;
  missingTopic: string;
}

function or(value: string, fallback: string): string {
  const clean = value.trim();
  return clean === "" ? fallback : clean;
}

/** The topic's own questions, or the two template questions with its name filled in. */
export function mainQuestionsFor(
  topic: InterviewTopic,
  p: InterviewPhrases
): string[] {
  const own = listItemsOf(topic.questions);
  if (own.length > 0) {
    return own;
  }
  const name = or(topic.name, p.missingTopic);
  return p.mainTemplates.map((template) => t(template, { topic: name }));
}

/** Topics the researcher has started — a row with a name or questions. */
export function activeTopics(input: InterviewInput): InterviewTopic[] {
  return input.topics.filter(
    (topic) => topic.name.trim() !== "" || topic.questions.trim() !== ""
  );
}

function metaLines(input: InterviewInput, p: InterviewPhrases): string[] {
  const lines = [
    t(p.metaStudy, { value: or(input.title, p.missingTitle) }),
    t(p.metaQuestion, {
      value: or(input.researchQuestion, p.missingQuestion),
    }),
    t(p.metaType, {
      value: input.type === "structured" ? p.typeStructured : p.typeSemi,
    }),
  ];
  if (input.minutes) {
    lines.push(t(p.metaLength, { minutes: input.minutes }));
  }
  return lines;
}

function introSection(input: InterviewInput, p: InterviewPhrases): DocSection {
  const interviewer = input.interviewer.trim();
  const institution = input.institution.trim();
  const sentences = [p.introThanks];
  if (interviewer && institution) {
    sentences.push(t(p.introNameInstitution, { interviewer, institution }));
  } else if (interviewer) {
    sentences.push(t(p.introName, { interviewer }));
  }
  sentences.push(t(p.introAbout, { about: or(input.about, p.missingAbout) }));
  if (input.minutes) {
    sentences.push(t(p.introTime, { minutes: input.minutes }));
  }
  sentences.push(p.introNoRight);
  const blocks: DocBlock[] = [{ kind: "p", text: sentences.join(" ") }];
  if (input.type === "structured") {
    blocks.push({ kind: "p", text: p.introStructured });
  }
  return { heading: p.introHeading, blocks };
}

const RECORDING_KEYS = {
  audio: "consentAudio",
  video: "consentVideo",
  none: "consentNotes",
} as const;

function consentSection(
  input: InterviewInput,
  p: InterviewPhrases
): DocSection {
  return {
    heading: p.consentHeading,
    blocks: [
      {
        kind: "p",
        text: [
          p.consentRead,
          p.consentVoluntary,
          p[RECORDING_KEYS[input.recording]],
          p.consentStart,
        ].join(" "),
      },
    ],
  };
}

function semiTopicSections(
  topics: InterviewTopic[],
  p: InterviewPhrases
): DocSection[] {
  return topics.map((topic, index) => {
    const name = or(topic.name, p.missingTopic);
    return {
      heading: t(p.topicHeading, { number: index + 1, topic: name }),
      blocks: [
        { kind: "p", text: p.mainLead },
        { kind: "ol", items: mainQuestionsFor(topic, p) },
        { kind: "p", text: p.followUpLead },
        {
          kind: "ul",
          items: p.followUpTemplates.map((template) =>
            t(template, { topic: name })
          ),
        },
      ],
    };
  });
}

function structuredTopicSections(
  topics: InterviewTopic[],
  p: InterviewPhrases
): DocSection[] {
  let next = 1;
  return topics.map((topic, index) => {
    const questions = mainQuestionsFor(topic, p);
    const block: DocBlock = { kind: "ol", items: questions, start: next };
    next += questions.length;
    return {
      heading: t(p.topicHeading, {
        number: index + 1,
        topic: or(topic.name, p.missingTopic),
      }),
      blocks: [block],
    };
  });
}

/** The whole guide, in the order the interview runs. */
export function buildInterviewGuide(
  input: InterviewInput,
  p: InterviewPhrases
): DocModel {
  const topics = activeTopics(input);
  const shown =
    topics.length > 0 ? topics : [{ id: "empty", name: "", questions: "" }];
  const structured = input.type === "structured";
  const sections: DocSection[] = [
    {
      heading: p.beforeHeading,
      blocks: [{ kind: "ul", items: [...p.beforeItems] }],
    },
    introSection(input, p),
    consentSection(input, p),
    {
      heading: p.warmUpHeading,
      blocks: [
        {
          kind: "ol",
          items: [...p.warmUpDefaults, ...listItemsOf(input.warmUp)],
        },
      ],
    },
  ];
  if (structured) {
    sections.push({
      heading: p.structuredHeading,
      blocks: [
        { kind: "p", text: p.structuredLead },
        { kind: "ul", items: [...p.structuredProbes] },
      ],
    });
    sections.push(...structuredTopicSections(shown, p));
  } else {
    sections.push({
      heading: p.probesHeading,
      blocks: [
        { kind: "p", text: p.probesLead },
        { kind: "ul", items: [...p.probes] },
      ],
    });
    sections.push(...semiTopicSections(shown, p));
  }
  sections.push(
    {
      heading: p.closingHeading,
      blocks: [{ kind: "ul", items: [...p.closingItems] }],
    },
    {
      heading: p.afterHeading,
      blocks: [{ kind: "ul", items: [...p.afterItems] }],
    }
  );
  return { title: p.docTitle, meta: metaLines(input, p), sections };
}

/** How many main questions the guide asks, across every topic. */
export function countMainQuestions(
  input: InterviewInput,
  p: InterviewPhrases
): number {
  return activeTopics(input).reduce(
    (sum, topic) => sum + mainQuestionsFor(topic, p).length,
    0
  );
}

/* ---------------------------------------------------------- question checks */

export type QuestionIssue =
  | "multiple"
  | "closed"
  | "leading"
  | "double"
  | "why"
  | "long";

/** The word lists the checks read — the widget's strings, so a language can bring its own. */
export interface QuestionWordLists {
  closedStarters: readonly string[];
  /** Starters that can open a polite invitation: "can", "could", "would", "will". */
  invitationStarters: readonly string[];
  openRequests: readonly string[];
  leadingPhrases: readonly string[];
  conjunctions: readonly string[];
  whyWords: readonly string[];
}

export interface QuestionCheck {
  question: string;
  issues: QuestionIssue[];
}

const QUESTION_MARK = /[?？]/g;
const LONG_QUESTION_WORDS = 25;

/**
 * "Can you tell me…", "Could you describe…" and "Would you walk me through…" open with
 * a yes/no verb but work as invitations, so an invitation starter followed by "you" and
 * a telling verb is not flagged. "Don't you think…" is not an invitation.
 */
function isClosed(words: string[], lists: QuestionWordLists): boolean {
  const [first, second, third] = words;
  if (!(first && lists.closedStarters.includes(first))) {
    return false;
  }
  const invitation =
    lists.invitationStarters.includes(first) &&
    second === "you" &&
    third !== undefined &&
    lists.openRequests.includes(third);
  return !invitation;
}

/** The problems a question shows, in the order they are worth fixing. */
export function checkQuestion(
  question: string,
  lists: QuestionWordLists
): QuestionIssue[] {
  const words = wordsOf(question);
  const issues: QuestionIssue[] = [];
  if ((question.match(QUESTION_MARK) ?? []).length > 1) {
    issues.push("multiple");
  }
  if (isClosed(words, lists)) {
    issues.push("closed");
  }
  if (lists.leadingPhrases.some((phrase) => containsPhrase(question, phrase))) {
    issues.push("leading");
  }
  if (lists.conjunctions.some((word) => words.includes(word))) {
    issues.push("double");
  }
  if (words[0] !== undefined && lists.whyWords.includes(words[0])) {
    issues.push("why");
  }
  if (words.length > LONG_QUESTION_WORDS) {
    issues.push("long");
  }
  return issues;
}

/** Every question the researcher wrote themselves, with any issues found. */
export function checkGuideQuestions(
  input: InterviewInput,
  lists: QuestionWordLists
): QuestionCheck[] {
  const own = new Set([
    ...listItemsOf(input.warmUp),
    ...input.topics.flatMap((topic) => listItemsOf(topic.questions)),
  ]);
  return [...own]
    .map((question) => ({ question, issues: checkQuestion(question, lists) }))
    .filter((check) => check.issues.length > 0);
}
